// @flow
import * as React from "react";
import PropTypes from 'prop-types';
import {autorun, action} from "mobx";
import {reisy, use} from "reisy";

import Arrow from "./arrow.js";
import {isMobile, isIOS} from "../environment.js";
import SpringSystem from "../pagehandler/math/springsystem.js";

import EventBinder from "./events.js";
import type {Direction, Edges} from "./utils.js";
import {Position, findEdges, findEdgeIndex, findNearestEdge} from "./utils.js";

import {ScrollHandler, Handlers, SCROLL_HANDLERS, SCROLL_HANDLERS_NATIVE} from "./handlers.js";

export const styles = reisy`
@namespace Scroller;
NavigationOffsetThreshold: 150;
JackInTheBoxScrollCorrection: $Settings.JackInTheBoxScrollCorrection;

Container {
	position: relative;
	display: flex;
	flex-shrink: 0;

	min-width: 100%;
	min-height: 100%;

	width: 100%;
	outline: none;
}

isChild {
	background: white;
}

scrolls {
	max-width: 100vw;
	max-height: 100vh;

	will-change: scroll-position;
	-ms-overflow-style: -ms-autohiding-scrollbar;
	-webkit-overflow-scrolling: touch;
}

ContainerV {
	@extends Container;
	flex-direction: column;
	overflow: hidden;

	&$scrolls {
		overflow-x: hidden;
		overflow-y: auto;
	}

	&$isChild {
		width: auto;
		height: auto;
		max-width: none;
		max-height: none;

		overflow-x: hidden;
		overflow-y: auto;

		will-change: scroll-position;
		-ms-overflow-style: -ms-autohiding-scrollbar;
		-webkit-overflow-scrolling: touch;
	}
}
ContainerH {
	@extends Container;
	flex-direction: row;
	overflow-x: auto;
	overflow-y: hidden;

	will-change: scroll-position;
	-ms-overflow-style: -ms-autohiding-scrollbar;
	-webkit-overflow-scrolling: touch;

	height: 100vh;

	&$isChild {
		width: auto;
		height: auto;
		max-width: none;
		max-height: none;
	}

	@media print {
		flex-direction: column;
		height: auto;

		& > * {
			page-break-after: always;
		}
	}
}
`;

type Props = {
	direction: "vertical" | "horizontal";
	isSinglePage?: boolean;
	onScrollTrigger?: () => any;
	containScroll?: boolean;
	position?: Position;
	zIndex?: number;
	makePositionLink?: (position: number) => string;
	onPositionChange?: (position: number) => void;
	children?: Array<React.Element<*>>;
};

class Scroller extends React.Component {
	props: Props;
	static contextTypes = {
		player: PropTypes.object,
	};

	binder = new EventBinder();
	elem: HTMLElement;
	setElem = (el: HTMLElement) => (this.elem = el);

	dispose: () => void = () => {};
	position: Position;
	isChild: boolean;
	isSinglePage: boolean;
	snapToEdges: boolean;
	horizontal: boolean;
	containScroll: boolean;

	lastWidth: number = 0;
	edges: Edges;

	springTarget: number;
	nextSpringScroll: number = 0;
	lastScroll: number = 0;

	scrollHandler: ScrollHandler;
	scrollHandlers: Array<ScrollHandler> = [];

	// TODO:
	spring: any;

	constructor(props: Props) {
		super(props);

		this.binder.addBinding("scroll", this.handleScroll);

		this.componentWillReceiveProps(props);
	}

	initScrollHandlers() {
		const {horizontal, isSinglePage, maxScroll} = this;
		const useNative = !maxScroll || (!horizontal && isSinglePage);
		const HANDLERS = useNative ? SCROLL_HANDLERS_NATIVE : SCROLL_HANDLERS;
		Handlers.unmount(this.scrollHandlers);
		this.scrollHandlers = HANDLERS.map(Handler => new Handler(this));
		this.scrollHandler = this.scrollHandlers[0];
		Handlers.mount(this.scrollHandlers);
	}

	componentDidMount() {
		if (!IS_BROWSER) { return; }
		window.addEventListener("resize", this.handleResize);
		this.elem.addEventListener('touchstart', this.handleTouch);
		this.elem.addEventListener('touchmove', this.handleTouch);
		const eventTarget = (this.horizontal || this.isChild || this.containScroll) ? this.elem : window;
		this.binder.setEventTarget(eventTarget);

		this.spring = new SpringSystem({
			scroll: val => {
				// XXX: it may be bad for performance to write and then read this prop,
				// but we do need to rely on the browsers internal rounding here, which
				// may be different than `Math.round` when the page is zoomed on
				// desktop!
				this.currentScroll = val;
				this.nextSpringScroll = this.currentScroll;
			},
		});

		this.handleResize(true);
	}

	componentWillReceiveProps(props: Props) {
		this.isSinglePage = props.isSinglePage !== false;
		this.snapToEdges = props.snapToEdges !== false;
		this.isChild = Boolean(this.props.onScrollTrigger);
		this.horizontal = props.direction === "horizontal";
		//containScroll is pretty much equivalent to isChild but just in case...
		this.containScroll = props.containScroll === true;
		const position = props.position || this.position || new Position();
		if (position !== this.position) {
			this.position = position;
			this.dispose();
			this.dispose = autorun("Scroller.handleResize", this.handleResize);
		}
	}

	componentDidUpdate() {
		const eventTarget = (this.horizontal || this.isChild || this.containScroll) ? this.elem : window;
		this.binder.setEventTarget(eventTarget);
		this.handleResize(true);
	}

	componentWillUnmount() {
		window.removeEventListener("resize", this.handleResize);
		this.elem.removeEventListener('touchstart', this.handleTouch);
		this.elem.removeEventListener('touchmove', this.handleTouch);
		this.binder.setEventTarget(null);

		this.spring.stop(false);

		Handlers.unmount(this.scrollHandlers);
	}

	get currentScroll(): number {
		const {isChild, elem, horizontal, containScroll} = this;
		let value;
		if (horizontal) {
			value = elem.scrollLeft;
		} else if (isChild || containScroll) {
			value = elem.scrollTop;
		} else if (
			/*document.body.scrollHeight > document.body.offsetHeight*/
			/* see below */
			document.documentElement && document.body.scrollHeight - 10 > document.documentElement.scrollHeight
		) {
			value = document.body.scrollTop;
		} else {
			value = window.pageYOffset;
		}
		return value;
	}
	set currentScroll(value: number) {
		const {isChild, elem, horizontal, containScroll} = this;
		if (horizontal) {
			elem.scrollLeft = value;
		} else if (isChild || containScroll) {
			elem.scrollTop = value;
		} else if (
			/*document.body.scrollHeight > document.body.offsetHeight*/
			/* this seems to be a better way of detecting cases where the body is set
			* to have 100% window height, but with "overflow: scroll"
			*/
			document.documentElement && document.body.scrollHeight - 10 > document.documentElement.scrollHeight
		) {
			document.body.scrollTop = value;
		} else {
			let __supportsScrollToWithOpts = "scrollBehavior" in document.documentElement.style;
		
			if (__supportsScrollToWithOpts) {
			  try {
				window.scrollTo({ top: value, behavior: "instant" });
			  } catch (e) {
				__supportsScrollToWithOpts = false;
			  }
			}
		
			if (!__supportsScrollToWithOpts) {
			  window.scrollTo(0, value);
			}
		}
	}

	get scrollWindow(): number {
		const {isChild, elem, horizontal, containScroll} = this;
		let value;
		if (horizontal) {
			value = elem.clientWidth;
		} else if (isChild || containScroll) {
			value = elem.clientHeight;
		} else if (
			/*document.body.scrollHeight > document.body.offsetHeight*/
			/* see below */
			document.documentElement && document.body.scrollHeight - 10 > document.documentElement.scrollHeight
		) {
			value = document.body.clientHeight;
		} else {
			value = window.innerHeight;
		}
		return value;
	}

	get maxScroll(): number {
		const {isChild, elem, horizontal, scrollWindow, containScroll} = this;
		let size = window.innerHeight;
		if (horizontal) {
			size = elem.scrollWidth;
		} else if (isChild || containScroll) {
			size = elem.scrollHeight;
		} else if (
			/*document.body.scrollHeight > document.body.offsetHeight*/
			/* see below */
			document.documentElement && document.body.scrollHeight - 10 > document.documentElement.scrollHeight
		) {
			size = document.body.scrollHeight;
		} else if (document.documentElement) {
			size = Math.max(document.documentElement.offsetHeight, document.documentElement.scrollHeight);
		}
		return size - scrollWindow;
	}

	handleArrow(direction: Direction, ev: Event) {
		ev.preventDefault();
		const position = this.position.target;
		const target = this.spring.running ? this.springTarget : this.currentScroll;
		const freeScroll = Math.abs(this.getMaxFreeScrollInDirection(direction, target));
		const sign = direction === "start" ? -1 : 1;
		if (freeScroll) {
			const offset = this.scrollWindow * 0.75;
			this.scrollToOffset(target + (sign * Math.min(freeScroll, offset)), true, position);
		} else {
			this.snapToPosition(position + sign, direction === "start" ? "end" : "start");
		}
	}

	handleStartArrow = (ev: Event) => this.handleArrow("start", ev);
	handleEndArrow = (ev: Event) => this.handleArrow("end", ev);

	handleResize = (_forceScroll: any) => {
		const forceScroll = _forceScroll === true;
		const {elem, horizontal, position} = this;
		const {scroll} = position;
		if (!elem) { return; }
		let edges = findEdges(elem, horizontal);

		if (this.isSinglePage && !horizontal && edges.length > 2) {
			edges = [edges[0], ...edges.slice(-1)];
		}
		this.edges = edges;

		// In case we are scrolling on the body, adjust all the adjust by the top
		// edge of the outer scroller.
		let top = 0;
		if (!horizontal && !this.isChild && !this.containScroll) {
			let parent: HTMLElement = elem;
			while (parent && (parent = (parent.offsetParent: any))) {
				top += parent.offsetTop;
			}

			if (this.context.player.options.jackInTheBox) {
				top += styles.JackInTheBoxScrollCorrection || 0;
			}
			
			// If we are embedded somewhere we shouldn't mess with the scrolling too much
			if (this.context.player.options.jackInTheBox && top > 0 && (!forceScroll || !!scroll)) { return; }

			for (let i = 0; i < edges.length; i++) {
				edges[i] += top;
			}
		}

		this.initScrollHandlers();

		// On mobile, when vertical scrolling hides the browser chrome, we will
		// get a resize event, but we do not want to snap in that case. rather want
		// to delegate that to the `Manual` scroll handler with a timeout.
		if (!forceScroll && !this.isChild && !this.containScroll && isMobile && this.lastWidth === window.innerWidth) { return; }
		this.lastWidth = window.innerWidth;

		// XXX: on initial load (when forceScroll is set), at position 0, force a
		// scroll to offset 0.
		// https://gitlab.pagestrip.com/pagestrip/kent-next/issues/539#note_12299
		if (forceScroll && scroll === 0 && (this.currentScroll === 0 || !horizontal && isMobile && !this.snapToEdges)) {
			this.scrollToOffset(0, false);
			return;
		}

		if(!this.snapToEdges) {
			return;
		}

		const snap = () => {
			const edge = this.findNearestEdge();
			if (forceScroll || edge.position !== scroll || edge.direction !== "middle") {
				this.snapToPosition(scroll, "start", false);
			}
		}

		if (this.context.player.options.jackInTheBox && !horizontal) {
			if( top > this.currentScroll) { 
				//don't scroll to top of story if the current scrollposition is above the story top
				return; 
			} else if (top < styles.NavigationOffsetThreshold) {
				// in some cases the embed is just shifted a bit because of a navigation element. 
				// simply snap to the actual top of the document anyway
				this.currentScroll = 0;
				this.lastScroll = 0;
				this.spring.reset({scroll: 0});
				return;
			} else {
				this.currentScroll = top - 1;
				this.lastScroll = top - 1;
				this.spring.reset({scroll: top - 1});
				setTimeout(snap, 0);
				return;
			}
		} 

		snap();
	}

	handleTouch = () => {
		if (this.spring.running) {
			this.spring.stop();
			this.handleScroll();
		}
	}

	handleScroll = () => {
		const {spring, lastScroll, currentScroll, scrollHandler = {}, nextSpringScroll} = this;
		this.lastScroll = currentScroll;

		if (spring.running) {
			//we can't trust the scroll position of iOS11+ anymore -> cancel via touch
			if (isIOS || currentScroll === nextSpringScroll) { return; }
			spring.stop();
		}

		const nearestPosition = this.findNearestEdge().position;
		if (this.position.target !== nearestPosition) {
			this.setTargetPosition(nearestPosition);
		}

		if (lastScroll === currentScroll) { return; }
		if (scrollHandler.handleScroll) {
			return scrollHandler.handleScroll(currentScroll, lastScroll);
		}
	}

	scrollToElem = (target: Element, position: number) => {
		const {horizontal, currentScroll, scrollWindow, maxScroll} = this;
		// If this Scroller is not able to scroll, delegate to the parent Scroller
		if (!maxScroll) {
			const {onScrollTrigger} = this.props;
			if (onScrollTrigger) {
				onScrollTrigger(target, position);
			}
			return;
		}

		const bbox = target.getBoundingClientRect();
		const offset = horizontal
			? bbox.left + (bbox.width * position)
			: bbox.top + (bbox.height * position);

		const scroll = currentScroll + offset - (scrollWindow * position);

		this.scrollToOffset(scroll);
	}

	snapToPosition(position: number, direction: Direction = "start", animate: boolean = true) {
		const {edges, currentScroll, scrollWindow} = this;
		if (position < 0 || position > edges.length - 2) { return; }

		const edge = direction === "start"
			? edges[position]
			: edges[position + 1] - scrollWindow;

		// Aligning the edge with the start edge of the scroll container may
		// overflow the scroll area, so make sure to clamp it.
		const scroll = Math.max(edges[0], Math.min(edge, edges[edges.length - 1] - scrollWindow));

		if (scroll === currentScroll) { return; }

		//console.log("snapping to %s %s", position, direction);

		this.scrollToOffset(scroll, animate, position);
	}

	scrollToOffset(scroll: number, animate: boolean = true, position?: number) {
		const {currentScroll} = this;
		if (scroll === currentScroll) { return; }

		if (typeof position === "number") {
			this.setTargetPosition(position);
		}
		if (animate || this.spring.running) {
			this.springTarget = scroll;
			if (!this.spring.running) {
				this.spring.reset({scroll: currentScroll});
			}
			this.spring.set({scroll}).then(() => {
				this.setScrollHandler();
				this.notifyPosition(this.position.target);
			});
		} else {
			this.lastScroll = scroll;
			this.spring.reset({scroll});
			if (typeof position === "number") {
				this.notifyPosition(position);
			}
		}
	}

	findNearestEdge(_start?: number) {
		const start = typeof _start === "number"
			? _start : this.currentScroll;
		const {edges, scrollWindow} = this;
		const end = start + scrollWindow;
		const edge = findNearestEdge(edges, start, end);
		if (start <= edges[0] || end >= edges[edges.length - 1]) {
			edge.direction = "middle";
		}
		return edge;
	}

	setScrollHandler(_handler?: ScrollHandler): boolean {
		const handler = _handler || this.scrollHandlers[0];
		if (this.scrollHandler === handler) { return true; }
		if (this.spring.running) { return false; }
		if (handler !== this.scrollHandlers[0] &&
			this.scrollHandler !== this.scrollHandlers[0]) {
			return false;
		}
		this.scrollHandler = handler;
		return true;
	}

	@action notifyPosition(position: number) {
		if (this.position.scroll === position) { return; }
		this.position.scroll = position;
		const {onPositionChange} = this.props;
		if (!onPositionChange) { return; }
		onPositionChange(position);
	}

	@action setTargetPosition(position: number) {
		this.position.target = position;
	}

	getMaxFreeScrollInDirection(direction: Direction, _lastScroll?: number) {
		const {edges} = this;
		const startEdge = typeof _lastScroll === "number" ? _lastScroll
			: (this.spring.running ? this.springTarget : this.currentScroll);
		const endEdge = startEdge + this.scrollWindow;

		if (startEdge < edges[0] || (direction === "start" && startEdge === edges[0])) {
			return (direction === "start" ? 0 : edges[0]) - startEdge;
		}
		if (endEdge > edges[edges.length - 1] || (direction === "end" && endEdge === edges[edges.length - 1])) {
			return (direction === "start" ? edges[edges.length - 1] : this.maxScroll + this.scrollWindow) - endEdge;
		}

		const start = findEdgeIndex(edges, startEdge, "start");
		const end = findEdgeIndex(edges, endEdge, "end");

		if (Math.abs(start.position - end.position) > 1) { return 0; }

		return direction === "end"
			? end.distance
			: start.distance;
	}

	clampOverflow(lastScroll: number, nextScroll: number, doScroll: boolean = true): number {
		const delta = nextScroll - lastScroll;
		const direction = delta < 0 ? "start" : "end";
		const maxFreeScroll = this.getMaxFreeScrollInDirection(direction, lastScroll);
		if (Math.abs(maxFreeScroll) < Math.abs(delta)) {
			if (doScroll) {
				this.scrollToOffset(lastScroll + maxFreeScroll, false);
			}
			return delta - maxFreeScroll;
		}
		return 0;
	}

	bounce(direction: Direction) {
		const spring = this.spring.springs.scroll;
		this.currentScroll += direction === "start" ? 10 : -10;
		this.snapToPosition(this.position.target, direction);
		spring.velocity = direction === "start" ? -500 : 500;
	}

	render() {
		const {isChild, horizontal, position, containScroll} = this;
		const {makePositionLink, children, zIndex, id} = this.props;

		return horizontal ? (
			<div id={id}>
				<Arrow
					direction="start"
					onClick={this.handleStartArrow}
					position={position}
					makeLink={makePositionLink} />
				<div
					{...use(
						styles.ContainerH,
						isChild && styles.isChild,
						containScroll && styles.scrolls,
						{zIndex})}
					ref={this.setElem}
					tabIndex="0">
					{React.Children.map(children, child =>
						child && React.cloneElement(child, {onScrollTrigger: this.scrollToElem, scrollParent: this}))}
				</div>
				<Arrow
					direction="end"
					onClick={this.handleEndArrow}
					position={position}
					makeLink={makePositionLink} />
			</div>
		) : (
			<div
				{...use(
					styles.ContainerV,
					isChild && styles.isChild,
					containScroll && styles.scrolls,
					{zIndex})}
				id={id}
				ref={this.setElem}
				tabIndex="0">
				{React.Children.map(children, child =>
					child && React.cloneElement(child, {onScrollTrigger: this.scrollToElem, scrollParent: this}))}
			</div>
		)
	}
}

export default Scroller;



// WEBPACK FOOTER //
// ./lib/scroller/index.js